import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Input, InputNumber, Modal, Select, Table, Tabs, Tag, message } from "antd";
import type { TableColumnsType } from "antd";
import { RefreshCw, Upload as UploadIcon } from "lucide-react";

import { Document, Page as PdfPage } from "react-pdf";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";

import { useGetCodeBookImportQuery } from "graphql/queries/get-code-book-import.generated";
import { useGetCodeBookImportPageLazyQuery } from "graphql/queries/get-code-book-import-page.generated";
import { useCodeBookImportStatusSubscription } from "graphql/subscriptions/code-book-import-status.generated";
import { useSaveCodeBookImportPageMutation } from "graphql/mutations/save-code-book-import-page.generated";
import { useRereadCodeBookImportMutation } from "graphql/mutations/reread-code-book-import.generated";
import { usePlanCodeBookPublishMutation } from "graphql/mutations/plan-code-book-publish.generated";
import { usePublishCodeBookImportMutation } from "graphql/mutations/publish-code-book-import.generated";
import { useRelinkCodeSectionOrphanMutation } from "graphql/mutations/relink-code-section-orphan.generated";
import { useSearchCodeSectionsLazyQuery } from "graphql/queries/search-code-sections.generated";

import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";
import { formatSectionNumberForCitation } from "utils/code-section-citation";

// The kind the reader assigned to each line. The reviewer can change it here.
//
// Corrections are stored per line (in code_book_import_pages.overrides), not as an edited
// copy of the page. That way, re-reading the PDF after improving a parser rule keeps the
// corrections the reviewer already made.
const LINE_KIND_OPTIONS = [
	{ value: "provision", label: "Provision" },
	{ value: "text", label: "Text" },
	{ value: "commentary", label: "Commentary" },
	{ value: "section", label: "Section heading" },
	{ value: "chapter", label: "Chapter heading" },
	{ value: "title", label: "Title" },
	{ value: "list", label: "List item" },
	{ value: "figure", label: "Figure" },
	{ value: "table", label: "Table" },
	{ value: "chrome", label: "Not part of the code" }
];

// Statuses that mean the server is still working on the run. While the run is in one of
// these, its provisions do not exist yet, so what the screen holds for them is empty.
const WORKING_STATUSES = new Set(["UPLOADED", "READING", "PUBLISHING"]);

// Colour for the "change" label of each provision, compared to the published book.
const CHANGE_COLOR: Record<string, string> = {
	ADDED: "green",
	REMOVED: "red",
	CHANGED: "gold",
	UNCHANGED: "default"
};

/** One line as the reader found it, stored in code_book_import_pages.lines. */
type ReadLine = {
	text: string;
	kind: string;
	page?: number;
};

/** A reviewer correction for one line: new text, new kind, or "remove this line". */
type LineCorrection = {
	text?: string;
	kind?: string;
	removed?: boolean;
};

/**
 * Detail screen for one code book import: what the reader extracted from the PDF, and
 * what publishing it would change.
 *
 * The three tabs answer three questions, in order:
 *   1. Text        — is the extracted text correct?
 *   2. Provisions  — are the parsed provisions correct?
 *   3. Citations   — which existing citations would publishing break?
 */
const PlanReviewCodeImport = () => {
	const { importId = "" } = useParams();
	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();

	const {
		data: importData,
		loading: isLoadingImport,
		refetch: refetchImport
	} = useGetCodeBookImportQuery({
		variables: { id: importId },
		skip: !importId,
		fetchPolicy: "cache-and-network"
	});

	// Reading the PDF takes seconds but uploading the figures takes minutes, so the screen
	// subscribes to the row's status instead of keeping a request open.
	const { data: liveStatusData } = useCodeBookImportStatusSubscription({
		variables: { id: importId },
		skip: !importId
	});

	const [loadImportPage, importPageQuery] = useGetCodeBookImportPageLazyQuery();
	const [saveImportPage] = useSaveCodeBookImportPageMutation();
	const [rereadImport] = useRereadCodeBookImportMutation();
	const [requestPublishPlan] = usePlanCodeBookPublishMutation();
	const [publishImport] = usePublishCodeBookImportMutation();
	const [relinkCitation] = useRelinkCodeSectionOrphanMutation();
	const [searchSections, sectionSearch] = useSearchCodeSectionsLazyQuery();

	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [corrections, setCorrections] = useState<Record<string, LineCorrection>>({});
	const [publishPlan, setPublishPlan] = useState<any>(null);
	const [isPublishing, setIsPublishing] = useState(false);

	const importRun = importData?.code_book_imports_by_pk;
	// The subscription carries the fields that change while the import runs; the query
	// carries everything else. Prefer the subscription when it has arrived.
	const status = liveStatusData?.code_book_imports_by_pk?.status ?? importRun?.status;
	// Tells Ollie which code book import is on screen.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen: importRun
				? `One code book import: the run that loaded ${[importRun.abbreviation, importRun.edition_year, importRun.title].filter(Boolean).join(" ")} into the platform, so its sections can be cited by a rule. Its status is ${status}.`
				: "",
			ids: [["code_book_imports.id", importId]]
		})
	);

	const metrics = liveStatusData?.code_book_imports_by_pk ?? importRun;

	// Pages are loaded one at a time. A single page holds every line the PDF printed, so
	// loading all of them at once would download most of the document.
	const openPage = useCallback(
		async (pageToOpen: number) => {
			setCurrentPageNumber(pageToOpen);
			const { data: loaded } = await loadImportPage({
				variables: { importId, pageNumber: pageToOpen }
			});
			setCorrections(loaded?.code_book_import_pages?.[0]?.overrides ?? {});
		},
		[importId, loadImportPage]
	);

	// The subscription only carries the row's own columns. The provisions and the lines of
	// the open page come from other queries, which ran while the import was still reading
	// and therefore got nothing. Without this, the Provisions tab stays at 0 and the Text
	// tab keeps showing the previous reading, until the user reloads the browser.
	//
	// Both are re-fetched once, on the move from a working status to a finished one, and
	// not on every subscription message: the metrics update many times while the figures
	// upload, and each re-fetch downloads every provision in the book.
	const previousStatus = useRef<string | undefined>(undefined);
	useEffect(() => {
		const wasWorking = Boolean(previousStatus.current && WORKING_STATUSES.has(previousStatus.current));
		previousStatus.current = status;
		if (!wasWorking || !status || WORKING_STATUSES.has(status)) return;
		refetchImport();
		openPage(currentPageNumber);
	}, [currentPageNumber, openPage, refetchImport, status]);

	// What the user has typed in the page box, which is not the page being shown until
	// they confirm it. It can be null while the box is empty mid-edit.
	const [typedPageNumber, setTypedPageNumber] = useState<number | null>(1);

	// Keep the box showing the page that is actually open, so cancelling an edit
	// (leaving the box empty, or typing a number out of range) does not leave a
	// number on screen that does not match what is below it.
	useEffect(() => {
		setTypedPageNumber(currentPageNumber);
	}, [currentPageNumber]);

	/** Opens the page typed in the box, ignoring anything outside the document. */
	const jumpToTypedPage = useCallback(() => {
		const lastPage = metrics?.page_count ?? 1;
		const requested = Number(typedPageNumber);
		if (!Number.isInteger(requested) || requested < 1 || requested > lastPage) {
			setTypedPageNumber(currentPageNumber);
			return;
		}
		if (requested !== currentPageNumber) openPage(requested);
	}, [currentPageNumber, metrics?.page_count, openPage, typedPageNumber]);

	const lines: ReadLine[] = useMemo(
		() => (importPageQuery.data?.code_book_import_pages?.[0]?.lines as ReadLine[]) ?? [],
		[importPageQuery.data]
	);

	// Corrections are keyed by the line's index inside `lines`, and merged so that
	// changing the kind does not erase an edit to the text.
	const applyCorrection = useCallback((lineIndex: number, change: LineCorrection) => {
		setCorrections(current => ({
			...current,
			[String(lineIndex)]: { ...(current[String(lineIndex)] ?? {}), ...change }
		}));
	}, []);

	const savePageCorrections = useCallback(async () => {
		if (!canManage) return blockChange();
		try {
			await saveImportPage({
				variables: { importId, pageNumber: currentPageNumber, overrides: corrections }
			});
			message.success(`Page ${currentPageNumber} saved.`);
		} catch (error: any) {
			message.error(error?.message ?? `Page ${currentPageNumber} could not be saved.`);
		}
	}, [blockChange, canManage, corrections, currentPageNumber, importId, saveImportPage]);

	// Opens the confirmation dialog with the pre-flight, so the user sees what publishing
	// would change before confirming it.
	const openPublishDialog = useCallback(async () => {
		if (!canManage) return blockChange();
		try {
			const { data: planned } = await requestPublishPlan({ variables: { importId } });
			setPublishPlan(planned?.plan_code_book_publish ?? null);
		} catch (error: any) {
			message.error(error?.message ?? "The publish check could not be run.");
		}
	}, [blockChange, canManage, importId, requestPublishPlan]);

	const confirmPublish = useCallback(async () => {
		setIsPublishing(true);
		try {
			const { data: published } = await publishImport({ variables: { importId } });
			const result = published?.publish_code_book_import;
			message.success(
				`Published ${result?.section_count} provisions. ` +
					`${result?.links_restored ?? 0} of ${result?.links_archived ?? 0} citations were restored automatically.`
			);
			setPublishPlan(null);
			await refetchImport();
		} catch (error: any) {
			message.error(error?.message ?? "The book could not be published.");
		} finally {
			setIsPublishing(false);
		}
	}, [importId, publishImport, refetchImport]);

	// The provisions table is paginated, and a code book has thousands of rows, so the
	// browser's own find (Ctrl+F) only searches the 50 rows currently on screen. This
	// filter searches all of them.
	const [provisionSearchTerm, setProvisionSearchTerm] = useState("");

	const matchingProvisions = useMemo(() => {
		const allProvisions = importRun?.sections ?? [];
		const term = provisionSearchTerm.trim().toLowerCase();
		if (!term) return allProvisions;
		// Number, title and body are searched together, so "1006.3", "egress" and a
		// phrase from the rule text all find the same provision.
		return allProvisions.filter((provision: any) =>
			[provision.section_number, provision.title, provision.body]
				.filter(Boolean)
				.some((field: string) => field.toLowerCase().includes(term))
		);
	}, [importRun?.sections, provisionSearchTerm]);

	const provisionColumns: TableColumnsType<any> = [
		{ title: "§", dataIndex: "section_number", key: "sectionNumber", width: 140 },
		{ title: "Title", dataIndex: "title", key: "title", width: 260 },
		{
			title: "Text",
			dataIndex: "body",
			key: "body",
			// Only a preview: a full provision can be several thousand characters.
			render: (body: string) => <span className="prci-body">{body?.slice(0, 260)}</span>
		},
		{ title: "Page", dataIndex: "page_number", key: "currentPageNumber", width: 80 },
		{
			title: "Against what is published",
			dataIndex: "change",
			key: "change",
			width: 170,
			render: (change: string | null) => (change ? <Tag color={CHANGE_COLOR[change]}>{change.toLowerCase()}</Tag> : "—")
		}
	];

	if (!isLoadingImport && !importRun) {
		return (
			<OllieLayout>
				<div className="prci-page">
					<Alert type="error" message="That import is not on file." />
				</div>
			</OllieLayout>
		);
	}

	return (
		<OllieLayout>
			<div className="prci-page">
				<div className="prci-header">
					<div>
						<h1>
							{importRun?.abbreviation}
							{importRun?.edition_year ? ` ${importRun.edition_year}` : ""}
						</h1>
						<p>{importRun?.title}</p>
					</div>
					<div className="prci-header-actions">
						<Tag>{status}</Tag>
						<Button
							icon={<RefreshCw size={16} />}
							disabled={!canManage || status === "READING"}
							onClick={async () => {
								await rereadImport({ variables: { importId } });
								message.info("Reading the PDF again.");
							}}
						>
							Read again
						</Button>
						<Button
							type="primary"
							icon={<UploadIcon size={16} />}
							disabled={!canManage || status !== "REVIEW"}
							onClick={openPublishDialog}
						>
							Publish
						</Button>
					</div>
				</div>
				{notice}

				{importRun?.error_message ? <Alert type="error" showIcon message={importRun.error_message} /> : null}

				<div className="prci-metrics">
					<span>
						<b>{metrics?.page_count ?? "—"}</b> pages
					</span>
					<span>
						<b>{metrics?.section_count ?? "—"}</b> provisions
					</span>
					<span
						className={metrics?.not_captured ? "prci-warn" : "prci-clean"}
						title="Lines that the page prints in the shape of a provision but that did not become a row. This is the measure to watch: the missing-parents count only ever detects a provision that has children of its own."
					>
						<b>{metrics?.not_captured ?? "—"}</b> not captured
					</span>
					<span>
						<b>{metrics?.missing_parents ?? "—"}</b> missing parents
					</span>
					<span>
						<b>{metrics?.figure_count ?? "—"}</b> figures
					</span>
					<span>commentary: {metrics?.commentary_family ?? "none"}</span>
				</div>

				<Tabs
					defaultActiveKey="text"
					onChange={key => {
						// Load page 1 the first time the Text tab is opened.
						if (key === "text" && !importPageQuery.data) openPage(1);
					}}
					items={[
						{
							key: "text",
							label: "Text",
							children: (
								<div className="prci-review">
									<div className="prci-lines">
										<div className="prci-page-nav">
											<Button
												size="small"
												disabled={currentPageNumber <= 1}
												onClick={() => openPage(currentPageNumber - 1)}
											>
												Previous
											</Button>
											<span className="prci-page-jump">
												Page{" "}
												<InputNumber
													size="small"
													min={1}
													max={metrics?.page_count ?? 1}
													value={typedPageNumber}
													// Only move when the user is done typing (Enter or
													// leaving the field). Reacting to every keystroke
													// would load page 1, then 10, then 100 while the
													// user types "100".
													onPressEnter={jumpToTypedPage}
													onBlur={jumpToTypedPage}
													onChange={value => setTypedPageNumber(value)}
													controls={false}
													className="prci-page-input"
												/>{" "}
												of {metrics?.page_count ?? "?"}
											</span>
											<Button
												size="small"
												disabled={currentPageNumber >= (metrics?.page_count ?? 1)}
												onClick={() => openPage(currentPageNumber + 1)}
											>
												Next
											</Button>
											<Button size="small" type="primary" disabled={!canManage} onClick={savePageCorrections}>
												Save corrections
											</Button>
										</div>

										{lines.map((line, lineIndex) => {
											// A correction, when there is one, replaces what the reader decided for this line.
											const correction = corrections[String(lineIndex)] ?? {};
											if (correction.removed) {
												return (
													<div className="prci-line prci-line-removed" key={lineIndex}>
														<span>{line.text}</span>
														<Button
															size="small"
															type="link"
															onClick={() => applyCorrection(lineIndex, { removed: false })}
														>
															Put back
														</Button>
													</div>
												);
											}
											return (
												<div className="prci-line" key={lineIndex}>
													<Select
														size="small"
														className="prci-line-kind"
														value={correction.kind ?? line.kind}
														options={LINE_KIND_OPTIONS}
														disabled={!canManage}
														onChange={kind => applyCorrection(lineIndex, { kind })}
													/>
													<span
														className={`prci-line-text prci-kind-${correction.kind ?? line.kind}`}
														contentEditable={canManage}
														suppressContentEditableWarning
														onBlur={event =>
															applyCorrection(lineIndex, {
																text: event.currentTarget.textContent ?? ""
															})
														}
													>
														{correction.text ?? line.text}
													</span>
													<Button
														size="small"
														type="link"
														disabled={!canManage}
														onClick={() => applyCorrection(lineIndex, { removed: true })}
													>
														Drop
													</Button>
												</div>
											);
										})}
									</div>

									{/* The same page of the source PDF, so the reviewer corrects the
									    text against what the code actually prints. */}
									<div className="prci-pdf">
										{importRun?.source_file_url ? (
											<Document
												file={importRun.source_file_url}
												loading="Opening the code…"
												error="The PDF could not be opened."
											>
												<PdfPage
													pageNumber={currentPageNumber}
													width={520}
													renderAnnotationLayer={false}
													renderTextLayer={false}
												/>
											</Document>
										) : null}
									</div>
								</div>
							)
						},
						{
							key: "sections",
							label: `Provisions (${importRun?.sections?.length ?? 0})`,
							children: (
								<>
									<div className="prci-provision-search">
										<Input.Search
											allowClear
											size="small"
											placeholder="Filter by number, title or text — for example 1006.3"
											value={provisionSearchTerm}
											onChange={event => setProvisionSearchTerm(event.target.value)}
										/>
										<span className="prci-provision-count">
											{matchingProvisions.length} of {importRun?.sections?.length ?? 0}
										</span>
									</div>
									<Table
										rowKey="id"
										size="small"
										columns={provisionColumns}
										dataSource={matchingProvisions}
										pagination={{ pageSize: 50 }}
									/>
								</>
							)
						},
						{
							key: "orphans",
							label: `Citations to put back (${importRun?.orphaned_links?.length ?? 0})`,
							children: (
								<>
									<Alert
										type="info"
										showIcon
										message="Publishing a code that is already in use replaces its provisions, so the citations that pointed at them are deleted. Citations whose section number did not change are restored automatically. These are the ones that need manual reconnection."
									/>
									<Table
										rowKey="id"
										size="small"
										dataSource={importRun?.orphaned_links ?? []}
										pagination={false}
										columns={[
											{
												title: "Rule",
												key: "rule",
												render: (_: any, row: any) => (
													<>
														<strong>{row.plan_review_requirement?.code}</strong>
														<div>{row.plan_review_requirement?.title}</div>
													</>
												)
											},
											{ title: "Cited", dataIndex: "citation_text", key: "cite" },
											{ title: "§", dataIndex: "section_number", key: "n" },
											{
												title: "Point it at",
												key: "relink",
												render: (_: any, row: any) => (
													<Select
														showSearch
														className="prci-relink"
														placeholder="Find a provision"
														filterOption={false}
														disabled={!canManage}
														onSearch={term =>
															importRun?.code_book_id
																? searchSections({
																		variables: {
																			codeBookId: importRun.code_book_id,
																			search: `${term}%`
																		}
																	})
																: undefined
														}
														options={(sectionSearch.data?.code_sections ?? []).map((section: any) => ({
															value: section.id,
															label: `${section.section_number} ${section.title ?? ""}`
														}))}
														onChange={async codeSectionId => {
															await relinkCitation({
																variables: { orphanId: row.id, codeSectionId }
															});
															message.success("Citation put back.");
															await refetchImport();
														}}
													/>
												)
											}
										]}
									/>
								</>
							)
						}
					]}
				/>

				<Modal
					title="Publish this code"
					open={Boolean(publishPlan)}
					onCancel={() => setPublishPlan(null)}
					onOk={confirmPublish}
					okText="Publish"
					okButtonProps={{ danger: !publishPlan?.first_import }}
					confirmLoading={isPublishing}
				>
					{publishPlan ? (
						<div className="prci-plan">
							<p>
								<b>{publishPlan.added}</b> new, <b>{publishPlan.changed}</b> changed, <b>{publishPlan.unchanged}</b>{" "}
								unchanged, <b>{publishPlan.removed}</b> no longer in the document.
							</p>
							{publishPlan.correction_problems?.length ? (
								<Alert
									type="warning"
									showIcon
									message="Some corrections did not take effect. Fix them on their page before publishing, or publish without them."
									description={
										<ul>
											{publishPlan.correction_problems.map((item: any, index: number) => (
												<li key={`${item.page_number}-${index}`}>
													Page {item.page_number}
													{item.line_text ? ` — “${item.line_text}”` : ""}: {item.problem}
												</li>
											))}
										</ul>
									}
								/>
							) : null}
							{publishPlan.first_import ? (
								<Alert
									type="success"
									showIcon
									message="No rule cites this code yet, so publishing cannot break anything."
								/>
							) : (
								<>
									<p>
										{publishPlan.links_on_file} citations point at this code today.{" "}
										{publishPlan.links_on_file - publishPlan.links_at_risk} come back on their own, because the citation
										is still written on the rule.
									</p>
									{publishPlan.links_at_risk ? (
										<Alert
											type="warning"
											showIcon
											message={`${publishPlan.links_at_risk} were chosen by hand and cannot be rebuilt from the rule's text.`}
											description={
												<ul>
													{publishPlan.at_risk?.map((link: any) => (
														<li key={`${link.rule_code}-${link.section_number}`}>
															{link.jurisdiction_name} — {link.rule_code}{" "}
															{formatSectionNumberForCitation(link.section_number)}
														</li>
													))}
												</ul>
											}
										/>
									) : null}
									<p className="prci-plan-note">
										Any citation that is not restored automatically appears in the “Citations to put back” tab, where
										you can assign it to its new provision.
									</p>
								</>
							)}
						</div>
					) : null}
				</Modal>
			</div>
		</OllieLayout>
	);
};

export default PlanReviewCodeImport;
